import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://wellnessconnect.com';

// Organization Schema for the main website
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Wellness Connect',
  url: BASE_URL,
  logo: `${BASE_URL}/logo.png`,
  sameAs: [
    'https://facebook.com/wellnessconnect',
    'https://instagram.com/wellnessconnect',
    'https://twitter.com/WellnessConnect',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'hello@wellnessconnect.com',
    contactType: 'customer service',
    availableLanguage: ['English'],
  },
  description: "The global online wellness booking platform. Find and book spa treatments, massage therapy, yoga, fitness, and holistic wellness services.",
};

// WebSite Schema with SearchAction
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Wellness Connect',
  url: BASE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${BASE_URL}/browse?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

// Breadcrumb Schema Generator
export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.url}`,
    })),
  };
}

// Local Business Schema Generator
export function generateLocalBusinessSchema(business: {
  id: string;
  name: string;
  description?: string;
  address: string;
  city?: string;
  state?: string;
  country?: string;
  phone?: string;
  email?: string;
  category: string;
  latitude?: number;
  longitude?: number;
  operatingHours?: Record<string, { open: string; close: string; closed?: boolean }>;
  rating?: number;
  reviewCount?: number;
  priceRange?: string;
}) {
  const categoryTypeMap: Record<string, string> = {
    spa_wellness: 'DaySpa',
    massage_therapy: 'HealthAndBeautyBusiness',
    fitness_gym: 'HealthClub',
    fitness_personal_training: 'HealthClub',
    yoga_meditation: 'HealthClub',
    beauty_salon: 'BeautySalon',
    health_coaching: 'MedicalBusiness',
    skin_care_aesthetics: 'BeautySalon',
    barber_shop_grooming: 'BarberShop',
    nail_spa: 'NailSalon',
    sports_recovery_rehabilitation: 'MedicalBusiness',
  };

  const openingHours = business.operatingHours
    ? Object.entries(business.operatingHours)
        .filter(([_, hours]) => hours && !hours.closed)
        .map(([day, hours]) => {
          const dayAbbrev = day.slice(0, 2);
          return `${dayAbbrev} ${hours.open}-${hours.close}`;
        })
    : [];

  const addressParts = business.address.split(',').map(p => p.trim());
  const city = business.city || addressParts[addressParts.length - 2] || '';
  const state = business.state || addressParts[addressParts.length - 1] || '';

  return {
    '@context': 'https://schema.org',
    '@type': categoryTypeMap[business.category] || 'LocalBusiness',
    '@id': `${BASE_URL}/provider/${business.id}`,
    name: business.name,
    description: business.description,
    url: `${BASE_URL}/provider/${business.id}`,
    telephone: business.phone,
    email: business.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: addressParts[0],
      addressLocality: city,
      addressRegion: state,
      addressCountry: business.country || '',
    },
    ...(business.latitude && business.longitude && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: business.latitude,
        longitude: business.longitude,
      },
    }),
    ...(openingHours.length > 0 && { openingHours }),
    ...(business.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: business.rating.toFixed(1),
        reviewCount: business.reviewCount || 1,
        bestRating: 5,
        worstRating: 1,
      },
    }),
    priceRange: business.priceRange || '$$',
    paymentAccepted: 'Cash, Debit Card, Credit Card',
  };
}

// Service Schema Generator
export function generateServiceSchema(service: {
  name: string;
  description?: string;
  price: number;
  duration: number;
  businessName: string;
  businessId: string;
  category?: string;
  currency?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'LocalBusiness',
      name: service.businessName,
      url: `${BASE_URL}/provider/${service.businessId}`,
    },
    offers: {
      '@type': 'Offer',
      price: service.price,
      priceCurrency: service.currency || 'USD',
      availability: 'https://schema.org/InStock',
    },
    ...(service.category && { serviceType: service.category }),
  };
}

// FAQ Schema Generator
export function generateFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// Review Schema Generator
export function generateReviewSchema(review: {
  author: string;
  rating: number;
  comment: string;
  datePublished: string;
  businessName: string;
  businessId: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: review.author,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1,
    },
    reviewBody: review.comment,
    datePublished: review.datePublished,
    itemReviewed: {
      '@type': 'LocalBusiness',
      name: review.businessName,
      url: `${BASE_URL}/provider/${review.businessId}`,
    },
  };
}

// Blog Article Schema Generator
export function generateArticleSchema(article: {
  title: string;
  description: string;
  url: string;
  imageUrl?: string;
  author: string;
  datePublished: string;
  dateModified?: string;
  category?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    url: `${BASE_URL}${article.url}`,
    image: article.imageUrl || `${BASE_URL}/og-wellness-connect.png`,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Wellness Connect',
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/logo.png`,
      },
    },
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${BASE_URL}${article.url}`,
    },
    ...(article.category && { articleSection: article.category }),
  };
}

// How-To Schema for wellness guides
export function generateHowToSchema(howTo: {
  name: string;
  description: string;
  steps: { name: string; text: string }[];
  totalTime?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: howTo.name,
    description: howTo.description,
    step: howTo.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
    ...(howTo.totalTime && { totalTime: howTo.totalTime }),
  };
}

// Main Structured Data Component
export function MainStructuredData() {
  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
}
